import { effect, Injectable, signal, untracked } from '@angular/core';
import { select } from '@ngxs/store';
import {
  KnowledgeBaseArticleDTOListing,
  KnowledgeBaseCategoryDTO,
  KnowledgeBaseCategoryId,
} from '../../../../../common/dto/knowledge-base.dto';
import { deepCopy } from '../../../../../common/utils/object.utils';
import { compareRanksFrom } from '../../../../../common/utils/rank.utils';
import { AppState } from '../../app.state';
import { HelpSiteService } from '../../common/services/help-site.service';
import { RouteNavigationService } from '../../common/services/route-navigation.service';

export interface NavStateModel {
  readonly items: NavItemModel[];
}

export interface KnowledgeBaseArticleDTOListingWithUrl extends KnowledgeBaseArticleDTOListing {
  url: string;
}

export interface NavItemModel {
  category: KnowledgeBaseCategoryDTO;

  children: NavItemModel[];

  articles: KnowledgeBaseArticleDTOListingWithUrl[] | undefined;

  isOpen: boolean;
}

@Injectable()
export class NavState {
  private _state = signal<Readonly<NavStateModel>>({
    items: [],
  });

  private siteData = select(AppState.siteData);
  private openedArticle = select(AppState.articleOpened);

  constructor(
    private _helpSiteService: HelpSiteService,
    private _routeNavigationService: RouteNavigationService,
  ) {
    effect(() => {
      const categories = this.siteData()?.categories ?? [];

      untracked(() => {
        this.reset(categories);
      });
    });

    effect(async () => {
      const openedArticle = this.openedArticle();

      if (!openedArticle) {
        return;
      }

      await untracked(async () => {
        await this._modifyStateAsync(async state => {
          const categoriesToOpen = this._listParentCategories(state, openedArticle.categories[0].categoryId).reverse();

          await Promise.all(categoriesToOpen.map(category => {
            return (async () => {
              if (!category.articles) {
                category.articles = await this._getArticlesOfCategoryAsync(category.category.id);
              }

              category.isOpen = true;
            })();
          }));
        });
      });
    });
  }

  public get state() {
    return this._state.asReadonly();
  }

  public async loadItemArticlesAsync(categoryId: KnowledgeBaseCategoryId) {
    await this._modifyStateAsync(async state => {
      const item = this._findItemInState(state, categoryId);

      if (!item) {
        return;
      }

      if (!item.articles) {
        item.articles = await this._getArticlesOfCategoryAsync(item.category.id);
      }
    });
  }

  public async toggleItemOpenedAsync(categoryId: KnowledgeBaseCategoryId) {
    await this._modifyStateAsync(async state => {
      const item = this._findItemInState(state, categoryId);

      if (!item) {
        return;
      }

      item.isOpen = !item.isOpen;

      if (item.isOpen && !item.articles) {
        item.articles = await this._getArticlesOfCategoryAsync(item.category.id);
      }
    });
  }

  private async _getArticlesOfCategoryAsync(categoryId: KnowledgeBaseCategoryId): Promise<KnowledgeBaseArticleDTOListingWithUrl[]> {
    const articles = await this._helpSiteService.getArticlesOfCategoryAsync(categoryId);

    return articles.map(_ => {
      return {
        ..._,
        url: this._routeNavigationService.generateArticleUrl(_),
      };
    });
  }

  public reset(categories: KnowledgeBaseCategoryDTO[]) {
    this._state.set({
      items: createList(null),
    });

    function createList(parentId: KnowledgeBaseCategoryId | null): NavItemModel[] {
      return categories.filter(_ => _.parentCategoryId === parentId).map(_ => {
        return {
          category: _,
          isOpen: false,
          children: createList(_.id),
          articles: undefined,
        };
      }).sort((a, b) => compareRanksFrom(a.category, b.category));
    }
  }

  private _findItemInState(state: NavStateModel, id: KnowledgeBaseCategoryId): NavItemModel | undefined {
    return traverse(state.items);

    function traverse(items: NavItemModel[]): NavItemModel | undefined {
      for (const child of items) {
        if (child.category.id === id) {
          return child;
        }

        const result = traverse(child.children);

        if (result) {
          return result;
        }
      }

      return undefined;
    }
  }

  private async _modifyStateAsync(stateModifier: (state: NavStateModel) => Promise<void>) {
    const newState = deepCopy(this._state());

    await stateModifier(newState);

    this._state.set(newState);
  }

  private _listParentCategories(state: NavStateModel, startingCategory: KnowledgeBaseCategoryId): NavItemModel[] {
    const result: NavItemModel[] = [];

    let currentId: KnowledgeBaseCategoryId | undefined = startingCategory;
    let maxIteration = 100;

    do {
      const item = this._findItemInState(state, currentId);

      if (item) {
        result.push(item);
      }

      currentId = item?.category?.parentCategoryId;
    } while (currentId && maxIteration--);

    return result;
  }
}
